/**
 * LevNutri — Agenda da Nutricionista
 * Vistas: Semana (padrão) | Dia | Mês
 */
import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import ReactDOM from 'react-dom';
import { appointmentsService, patientsService, settingsService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../Modal/ConfirmModal';
import { Ico, emo } from '../ui/Ico';

// ── Brand ──────────────────────────────────────────────────────────────────────
const C = {
  primary: '#d4a574', dark: '#5a3e2b', darker: '#2f2a26',
  bg: '#fbf8f5', card: '#ffffff', border: '#f6f0ea',
  green: '#46714f', blue: '#7189a6', red: '#c46a60',
  yellow: '#c4933f', purple: '#7189a6',
};

// ── Grid constants ─────────────────────────────────────────────────────────────
const HOUR_H = 64;     // px per hour
const H_START = 7;     // 07:00
const H_END   = 20;    // 20:00
const HOURS   = Array.from({ length: H_END - H_START }, (_, i) => H_START + i);
const TOTAL_H = HOURS.length * HOUR_H;

// Modelos rápidos de consulta (modalidade + duração) para o modal de agendamento
const CONSULTA_TEMPLATES = [
  { label: 'Primeira presencial', icon: '🏥', tipo: 'presencial', duracao: 60 },
  { label: 'Primeira online',     icon: '💻', tipo: 'online',     duracao: 60 },
  { label: 'Retorno presencial',  icon: '🏥', tipo: 'presencial', duracao: 30 },
  { label: 'Retorno online',      icon: '💻', tipo: 'online',     duracao: 30 },
  { label: 'Bioimpedância',       icon: '⚖️', tipo: 'presencial', duracao: 45 },
];

// ── Labels ────────────────────────────────────────────────────────────────────
const DAY_SHORT  = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const MONTH_FULL = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                    'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const STATUS_CFG = {
  agendada:               { label: 'Agendada',       col: '#7189a6', bg: '#edf1f6', txt: '#4f6a86' },
  confirmada:             { label: 'Confirmada',     col: '#7189a6', bg: '#edf1f6', txt: '#4f6a86' },
  realizada:              { label: 'Realizada',      col: '#46714f', bg: '#edf4ee', txt: '#2f5236' },
  cancelada:              { label: 'Cancelada',      col: '#c46a60', bg: '#f8ebe9', txt: '#a8483f' },
  faltou:                 { label: 'Faltou',         col: '#c4933f', bg: '#f8f1e2', txt: '#8a5f1a' },
  aguardando_confirmacao: { label: 'Ag. PIX',        col: '#8a5f1a', bg: '#f8f1e2', txt: '#8a5f1a' },
};

const TIPO_CFG = {
  presencial: { label: 'Presencial', bg: '#edf4ee', border: '#46714f', txt: '#2f5236', icon: '🏥' },
  online:     { label: 'Online',     bg: '#edf1f6', border: '#7189a6', txt: '#4f6a86', icon: '💻' },
};

// ── Utilities ─────────────────────────────────────────────────────────────────
const toISO = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};

const parseDT = (s) => s ? new Date(s.replace(' ','T')) : null;

const fmtTime = (s) => {
  const d = parseDT(s); return d ? d.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}) : '';
};

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(v||0);

function getWeekMonday(date) {
  const d = new Date(date);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d;
}

function getWeekDays(monday) {
  return Array.from({length:6}, (_,i) => { const d=new Date(monday); d.setDate(d.getDate()+i); return d; });
}

function getMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month+1, 0);
  let dow = first.getDay(); dow = dow===0?6:dow-1;
  const cells = [];
  for (let i=dow-1;i>=0;i--) cells.push({date:new Date(year,month,-i), curr:false});
  for (let d=1;d<=last.getDate();d++) cells.push({date:new Date(year,month,d), curr:true});
  while (cells.length%7!==0) cells.push({date:new Date(year,month+1,cells.length-dow-last.getDate()+1),curr:false});
  const weeks=[];
  for (let i=0;i<cells.length;i+=7) weeks.push(cells.slice(i,i+7));
  return weeks;
}

function apptPos(dataHora, duracao) {
  const d = parseDT(dataHora); if (!d) return null;
  const startMin = d.getHours()*60+d.getMinutes();
  const endMin   = startMin+(duracao||60);
  const gs=H_START*60, ge=H_END*60;
  const cs=Math.max(startMin,gs), ce=Math.min(endMin,ge);
  if (cs>=ce) return null;
  return { top:((cs-gs)/60)*HOUR_H, height:Math.max(((ce-cs)/60)*HOUR_H, 26) };
}

function apptColors(appt) {
  if (appt.status==='aguardando_confirmacao')
    return { bg:'#f8f1e2', border:'#c4933f', txt:'#8a5f1a' };
  if (appt.status==='cancelada'||appt.status==='faltou')
    return { bg:'#fbf8f5', border:'#b8c7d8', txt:'#7189a6' };
  return TIPO_CFG[appt.tipo]
    ? { bg:TIPO_CFG[appt.tipo].bg, border:TIPO_CFG[appt.tipo].border, txt:TIPO_CFG[appt.tipo].txt }
    : { bg:'#f6f0ea', border:C.primary, txt:C.dark };
}

function buildWA(appt) {
  const d = parseDT(appt.data_hora);
  const phone = (appt.whatsapp||'').replace(/\D/g,'');
  if (!phone) return null;
  const dia  = d ? d.toLocaleDateString('pt-BR',{day:'numeric',month:'long'}) : '';
  const hora = fmtTime(appt.data_hora);
  const tipo = appt.tipo==='online'?'online':'presencial';
  const msg  = `Olá ${appt.patient_name}! Passando para confirmar sua consulta ${tipo} no dia ${dia} às ${hora}. Confirma? 😊`;
  return `https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`;
}

// ── Modal base ────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, wide }) {
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',
              alignItems:'center',justifyContent:'center',zIndex:9997,padding:'1rem'}}
    >
      <div
        onClick={e=>e.stopPropagation()}
        style={{background:'#ffffff',borderRadius:'14px',padding:'1.5rem',
                width:'100%',maxWidth:wide?'660px':'460px',maxHeight:'90vh',
                overflowY:'auto',boxShadow:'0 24px 64px rgba(0,0,0,.2)'}}
      >
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.25rem'}}>
          <h2 style={{margin:0,fontSize:'1.05rem',fontWeight:700,color:C.darker}}>{emo(title)}</h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.25rem',cursor:'pointer',color:'#a39b93',padding:0}}><Ico e="✕" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── Field helpers ─────────────────────────────────────────────────────────────
const inp = {padding:'0.5rem 0.7rem',border:`1px solid ${C.border}`,borderRadius:'8px',
             fontSize:'0.88rem',color:C.darker,background:C.bg,width:'100%',boxSizing:'border-box',outline:'none'};
const lbl = {fontSize:'0.73rem',fontWeight:600,color:C.dark,textTransform:'uppercase',letterSpacing:'.04em',display:'block',marginBottom:'0.25rem'};

// ── AppointmentBlock (week + day grids) ───────────────────────────────────────
// Suporta arrastar verticalmente para reagendar (snap de 15min). onReschedule
// recebe (appt, novoDataHora) — só dispara se o horário realmente mudou.
function ApptBlock({ appt, onClick, onReschedule, dayIso }) {
  const pos = apptPos(appt.data_hora, appt.duracao_minutos);
  const [dragDy, setDragDy] = useState(0);
  const dragState = useRef(null);
  if (!pos) return null;
  const col = apptColors(appt);
  const cancelled = appt.status==='cancelada'||appt.status==='faltou';
  const arrastavel = !!onReschedule && !cancelled && appt.status!=='realizada';

  const onPointerDown = (e) => {
    if (!arrastavel) return;
    e.stopPropagation();
    dragState.current = { startY: e.clientY, moved: false, pointerId: e.pointerId };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    const st = dragState.current; if (!st) return;
    const dy = e.clientY - st.startY;
    if (Math.abs(dy) > 4) st.moved = true;
    setDragDy(dy);
  };
  const onPointerUp = (e) => {
    const st = dragState.current; if (!st) { return; }
    dragState.current = null;
    try { e.currentTarget.releasePointerCapture(st.pointerId); } catch {}
    const dy = e.clientY - st.startY;
    setDragDy(0);
    if (!st.moved || Math.abs(dy) < 8) { onClick(appt); return; } // clique, não arrasto
    // dy px → minutos (HOUR_H px = 60min), snap 15min
    const deltaMin = Math.round((dy / HOUR_H) * 60 / 15) * 15;
    if (deltaMin === 0) return;
    const d = new Date(appt.data_hora.replace(' ', 'T'));
    d.setMinutes(d.getMinutes() + deltaMin);
    const p2 = (n) => String(n).padStart(2,'0');
    const novo = `${d.getFullYear()}-${p2(d.getMonth()+1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}:00`;
    onReschedule(appt, novo);
  };

  return (
    <div
      onClick={arrastavel ? undefined : e=>{e.stopPropagation();onClick(appt);}}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      title={`${fmtTime(appt.data_hora)} ${appt.patient_name}${arrastavel?' — arraste para reagendar':''}`}
      style={{
        position:'absolute', left:2, right:2,
        top:pos.top + dragDy, height:pos.height,
        background:col.bg, border:`1.5px solid ${col.border}`,
        borderRadius:'6px', padding:'3px 6px', overflow:'hidden',
        cursor:arrastavel?'grab':'pointer', zIndex:dragDy?5:1, opacity:cancelled?.65:1,
        boxShadow:dragDy?'0 4px 12px rgba(0,0,0,.18)':'none', touchAction:'none',
      }}
    >
      <p style={{margin:0,fontSize:'0.7rem',fontWeight:600,color:col.txt,
                 overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis',
                 textDecoration:cancelled?'line-through':'none'}}>
        {emo(appt.status==='aguardando_confirmacao' ? '⏳ ' : '')}{fmtTime(appt.data_hora)} {appt.patient_name}
      </p>
      {pos.height > 42 && (
        <p style={{margin:'1px 0 0',fontSize:'0.63rem',color:col.txt,opacity:.75,
                   overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
          {appt.status==='aguardando_confirmacao' ? 'Ag. PIX' : `${TIPO_CFG[appt.tipo]?.icon||''} ${appt.duracao_minutos}min`}
        </p>
      )}
    </div>
  );
}

// ── Current time line ─────────────────────────────────────────────────────────
function NowLine({ show }) {
  const [top, setTop] = useState(null);
  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const min = now.getHours()*60+now.getMinutes();
      const gs  = H_START*60, ge = H_END*60;
      if (min>=gs && min<=ge) setTop(((min-gs)/60)*HOUR_H);
      else setTop(null);
    };
    calc();
    const t = setInterval(calc, 60000);
    return ()=>clearInterval(t);
  }, []);
  if (!show || top===null) return null;
  return (
    <div style={{position:'absolute',left:0,right:0,top,height:2,background:'#c46a60',zIndex:3,pointerEvents:'none'}}>
      <div style={{position:'absolute',left:-4,top:-3,width:8,height:8,borderRadius:'50%',background:'#c46a60'}}/>
    </div>
  );
}

// ── WEEK VIEW ─────────────────────────────────────────────────────────────────
function WeekView({ appointments, currentDate, onSlotClick, onApptClick, onReschedule, settings }) {
  const gridRef = useRef(null);
  const today   = toISO(new Date());
  const monday  = getWeekMonday(currentDate);
  const days    = getWeekDays(monday);

  // Derived from settings
  const hWorkStart = settings ? parseInt((settings.horario_inicio || '08:00').split(':')[0], 10) : 8;
  const hWorkEnd   = settings ? parseInt((settings.horario_fim    || '18:00').split(':')[0], 10) : 18;
  // PHP dias: 1=Seg,2=Ter,...,6=Sáb,7=Dom → JS getDay(): 1=Seg,...,6=Sáb,0=Dom
  const workingDays = new Set(
    (settings?.dias_atendimento || [1,2,3,4,5]).map(d => d === 7 ? 0 : d)
  );

  useEffect(() => {
    if (gridRef.current) {
      const scrollTo = Math.max(0, (hWorkStart - 1 - H_START) * HOUR_H - 20);
      gridRef.current.scrollTop = scrollTo;
    }
  }, [hWorkStart]); // eslint-disable-line

  return (
    <div style={{display:'flex',flexDirection:'column',border:`1px solid ${C.border}`,borderRadius:'12px',overflow:'hidden',background:C.card}}>
      {/* Header */}
      <div style={{display:'flex',borderBottom:`2px solid ${C.border}`,background:C.bg,flexShrink:0}}>
        <div style={{width:52,flexShrink:0}}/>
        {days.map((day,i) => {
          const iso = toISO(day);
          const isToday = iso===today;
          const isWorking = workingDays.has(day.getDay());
          return (
            <div key={i} style={{flex:1,textAlign:'center',padding:'0.6rem 0.25rem',
                                  borderLeft:`1px solid ${C.border}`,
                                  background: !isWorking ? '#fbf8f5' : 'transparent'}}>
              <p style={{margin:0,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'.06em',
                          color: isWorking ? '#a39b93' : '#d9d0c6'}}>
                {emo(DAY_SHORT[day.getDay()])}
              </p>
              <div style={{
                display:'inline-flex',alignItems:'center',justifyContent:'center',
                width:28,height:28,borderRadius:'50%',marginTop:'0.2rem',
                background:isToday?C.primary:'transparent',
                color:isToday?'#ffffff': isWorking ? C.darker : '#d9d0c6',
                fontSize:'0.88rem',fontWeight:600,
              }}>
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable grid */}
      <div ref={gridRef} style={{overflowY:'auto',maxHeight:'580px'}}>
        <div style={{display:'flex'}}>
          {/* Time column */}
          <div style={{width:52,flexShrink:0}}>
            {HOURS.map(h => (
              <div key={h} style={{height:HOUR_H,borderBottom:`1px solid ${C.border}`,
                                    display:'flex',alignItems:'flex-start',paddingTop:4,
                                    justifyContent:'flex-end',paddingRight:8}}>
                <span style={{fontSize:'0.68rem',
                              color: h >= hWorkStart && h < hWorkEnd ? '#a39b93' : '#d9d0c6'}}>
                  {String(h).padStart(2,'0')}h
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, di) => {
            const iso = toISO(day);
            const isToday = iso===today;
            const isWorking = workingDays.has(day.getDay());
            const dayAppts = appointments.filter(a => a.data_hora.startsWith(iso));
            return (
              <div key={di} style={{flex:1,position:'relative',height:TOTAL_H,
                                    borderLeft:`1px solid ${C.border}`,
                                    background: !isWorking ? '#fbf8f5' : (isToday ? '#ffffff' : 'transparent')}}>
                {/* Hour click zones */}
                {HOURS.map(h => {
                  const inSchedule = isWorking && h >= hWorkStart && h < hWorkEnd;
                  const offBg = !inSchedule ? 'rgba(0,0,0,0.025)' : 'transparent';
                  return (
                    <React.Fragment key={h}>
                      <div
                        onClick={inSchedule ? ()=>onSlotClick(iso,h,0) : undefined}
                        style={{position:'absolute',top:(h-H_START)*HOUR_H,height:HOUR_H/2,width:'100%',
                                borderBottom:`1px dashed ${C.border}`,
                                cursor:inSchedule?'pointer':'default',
                                background:offBg}}/>
                      <div
                        onClick={inSchedule ? ()=>onSlotClick(iso,h,30) : undefined}
                        style={{position:'absolute',top:(h-H_START)*HOUR_H+HOUR_H/2,height:HOUR_H/2,width:'100%',
                                borderBottom:`1px solid ${C.border}`,
                                cursor:inSchedule?'pointer':'default',
                                background:offBg}}/>
                    </React.Fragment>
                  );
                })}
                {/* Current time */}
                <NowLine show={isToday}/>
                {/* Appointments */}
                {dayAppts.map(a=><ApptBlock key={a.id} appt={a} onClick={onApptClick} onReschedule={onReschedule}/>)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── DAY VIEW ──────────────────────────────────────────────────────────────────
function DayView({ appointments, currentDate, onSlotClick, onApptClick, onReschedule, settings }) {
  const gridRef = useRef(null);
  const iso    = toISO(currentDate);
  const today  = toISO(new Date());
  const isToday = iso===today;
  const dayAppts = appointments.filter(a => a.data_hora.startsWith(iso));

  const hWorkStart = settings ? parseInt((settings.horario_inicio || '08:00').split(':')[0], 10) : 8;
  const hWorkEnd   = settings ? parseInt((settings.horario_fim    || '18:00').split(':')[0], 10) : 18;
  const workingDays = new Set(
    (settings?.dias_atendimento || [1,2,3,4,5]).map(d => d === 7 ? 0 : d)
  );
  const dayOfWeek = currentDate.getDay();
  const isDayWorking = workingDays.has(dayOfWeek);

  useEffect(()=>{
    if (gridRef.current) {
      const scrollTo = Math.max(0, (hWorkStart - 1 - H_START) * HOUR_H - 20);
      gridRef.current.scrollTop = scrollTo;
    }
  },[hWorkStart]); // eslint-disable-line

  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:'12px',overflow:'hidden',background:C.card}}>
      <div ref={gridRef} style={{overflowY:'auto',maxHeight:'600px'}}>
        <div style={{display:'flex'}}>
          {/* Time column */}
          <div style={{width:52,flexShrink:0}}>
            {HOURS.map(h=>(
              <div key={h} style={{height:HOUR_H,borderBottom:`1px solid ${C.border}`,
                                    display:'flex',alignItems:'flex-start',paddingTop:4,
                                    justifyContent:'flex-end',paddingRight:8}}>
                <span style={{fontSize:'0.68rem',
                              color: h >= hWorkStart && h < hWorkEnd ? '#a39b93' : '#d9d0c6'}}>
                  {String(h).padStart(2,'0')}h
                </span>
              </div>
            ))}
          </div>
          {/* Single day column */}
          <div style={{flex:1,position:'relative',height:TOTAL_H,borderLeft:`1px solid ${C.border}`,
                       background: !isDayWorking ? '#fbf8f5' : (isToday ? '#ffffff' : 'transparent')}}>
            {HOURS.map(h=>{
              const inSchedule = isDayWorking && h >= hWorkStart && h < hWorkEnd;
              const offBg = !inSchedule ? 'rgba(0,0,0,0.025)' : 'transparent';
              return (
                <React.Fragment key={h}>
                  <div
                    onClick={inSchedule ? ()=>onSlotClick(iso,h,0) : undefined}
                    style={{position:'absolute',top:(h-H_START)*HOUR_H,height:HOUR_H/2,width:'100%',
                            borderBottom:`1px dashed ${C.border}`,
                            cursor:inSchedule?'pointer':'default',background:offBg}}/>
                  <div
                    onClick={inSchedule ? ()=>onSlotClick(iso,h,30) : undefined}
                    style={{position:'absolute',top:(h-H_START)*HOUR_H+HOUR_H/2,height:HOUR_H/2,width:'100%',
                            borderBottom:`1px solid ${C.border}`,
                            cursor:inSchedule?'pointer':'default',background:offBg}}/>
                </React.Fragment>
              );
            })}
            <NowLine show={isToday}/>
            {dayAppts.map(a=><ApptBlock key={a.id} appt={a} onClick={onApptClick} onReschedule={onReschedule}/>)}
          </div>
        </div>
      </div>
      {/* Day summary */}
      {dayAppts.length===0 && (
        <div style={{padding:'1.5rem',textAlign:'center',color:'#d9d0c6',borderTop:`1px solid ${C.border}`}}>
          Nenhuma consulta neste dia
        </div>
      )}
    </div>
  );
}

// ── MONTH VIEW ────────────────────────────────────────────────────────────────
function MonthView({ appointments, currentDate, onDayClick }) {
  const today = toISO(new Date());
  const y = currentDate.getFullYear(), m = currentDate.getMonth();
  const weeks = getMonthGrid(y, m);
  const apptMap = {};
  appointments.forEach(a => {
    const day = a.data_hora.slice(0,10);
    if (!apptMap[day]) apptMap[day]=[];
    apptMap[day].push(a);
  });

  return (
    <div style={{border:`1px solid ${C.border}`,borderRadius:'12px',overflow:'hidden',background:C.card}}>
      {/* Weekday headers */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:`2px solid ${C.border}`,background:C.bg}}>
        {['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'].map(d=>(
          <div key={d} style={{textAlign:'center',padding:'0.6rem 0',fontSize:'0.72rem',fontWeight:600,color:'#a39b93',textTransform:'uppercase'}}>
            {d}
          </div>
        ))}
      </div>
      {/* Weeks */}
      {weeks.map((week, wi)=>(
        <div key={wi} style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:`1px solid ${C.border}`}}>
          {week.map(({date,curr}, di)=>{
            const iso = toISO(date);
            const isToday = iso===today;
            const appts = apptMap[iso]||[];
            const realized=appts.filter(a=>a.status==='realizada');
            const upcoming=appts.filter(a=>['agendada','confirmada'].includes(a.status));
            return (
              <div key={di}
                onClick={()=>curr&&onDayClick(date)}
                style={{
                  minHeight:80,padding:'0.4rem',
                  background:isToday?'#fbf8f5':curr?C.card:'#fbf8f5',
                  borderLeft:di>0?`1px solid ${C.border}`:'none',
                  cursor:curr?'pointer':'default',
                  opacity:curr?1:.4,
                }}
              >
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.25rem'}}>
                  <span style={{
                    width:22,height:22,borderRadius:'50%',display:'flex',
                    alignItems:'center',justifyContent:'center',
                    background:isToday?C.primary:'transparent',
                    color:isToday?'#ffffff':C.darker,
                    fontSize:'0.8rem',fontWeight:isToday?700:400,
                  }}>
                    {date.getDate()}
                  </span>
                </div>
                {/* Appointment dots */}
                <div style={{display:'flex',flexWrap:'wrap',gap:'2px',marginTop:'0.2rem'}}>
                  {upcoming.slice(0,3).map(a=>(
                    <span key={a.id} title={`${fmtTime(a.data_hora)} ${a.patient_name}`}
                      style={{width:8,height:8,borderRadius:'50%',
                              background:a.tipo==='online'?C.blue:C.green,display:'block'}}/>
                  ))}
                  {realized.slice(0,3).map(a=>(
                    <span key={a.id} title={`${fmtTime(a.data_hora)} ${a.patient_name} `}
                      style={{width:8,height:8,borderRadius:'50%',background:'#6f9a77',display:'block'}}/>
                  ))}
                  {appts.length>6 && <span style={{fontSize:'0.6rem',color:'#736b64'}}>+{appts.length-6}</span>}
                </div>
                {/* First appt name (if not too small) */}
                {appts.length>0 && (
                  <p style={{margin:'0.2rem 0 0',fontSize:'0.65rem',color:'#736b64',
                             overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                    {fmtTime(appts[0].data_hora)} {appts[0].patient_name}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── NEW APPOINTMENT MODAL ─────────────────────────────────────────────────────
function NewApptModal({ onClose, onCreated, initDate, initHour, initMin, initPatient, patients, settings }) {
  const { showSuccess, showError } = useToast();
  const timeStr = `${String(initHour||8).padStart(2,'0')}:${String(initMin||0).padStart(2,'0')}`;
  const [form, setForm] = useState({
    patient_id: initPatient?.id || '', date: initDate||toISO(new Date()), time: timeStr,
    duracao_minutos:60, tipo:'presencial',
    valor_cobrado: settings?.valor_consulta_presencial||150, observacoes:'',
  });
  const [patQ, setPatQ] = useState(initPatient?.name || '');
  const [patOpen, setPatOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const patRef = useRef(null);

  // Update valor when tipo changes
  useEffect(()=>{
    if (!settings) return;
    setForm(prev=>({...prev,
      valor_cobrado: prev.tipo==='online'
        ?(settings.valor_consulta_online||120)
        :(settings.valor_consulta_presencial||150),
    }));
  },[form.tipo,settings]); // eslint-disable-line

  const filtPats = patients.filter(p=>patQ.length>1 && p.name.toLowerCase().includes(patQ.toLowerCase())).slice(0,8);

  const selectPat = (p) => { setForm(f=>({...f,patient_id:p.id})); setPatQ(p.name); setPatOpen(false); };
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async () => {
    if (!form.patient_id) { showError('Selecione um paciente'); return; }
    if (!form.date||!form.time) { showError('Data e hora são obrigatórios'); return; }
    setSaving(true);
    try {
      const res = await appointmentsService.create({
        patient_id: form.patient_id,
        data_hora:  `${form.date} ${form.time}:00`,
        duracao_minutos: parseInt(form.duracao_minutos,10),
        tipo: form.tipo,
        valor_cobrado: parseFloat(form.valor_cobrado)||null,
        observacoes: form.observacoes||null,
      });
      if (res.success) { showSuccess('Consulta agendada!'); onCreated(res.data); onClose(); }
      else showError(res.message||'Erro ao agendar');
    } catch(e) { showError(e.response?.data?.error||e.response?.data?.message||'Conflito de horário ou erro de conexão'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Nova Consulta" onClose={onClose}>
      <div style={{display:'flex',flexDirection:'column',gap:'0.85rem'}}>
        {/* Patient */}
        <div style={{position:'relative'}} ref={patRef}>
          <label style={lbl}>Paciente *</label>
          <input style={inp} placeholder="Buscar paciente..." value={patQ}
            onChange={e=>{setPatQ(e.target.value);setPatOpen(true);setForm(f=>({...f,patient_id:''}));}}
            onFocus={()=>setPatOpen(true)}
          />
          {patOpen && filtPats.length>0 && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#ffffff',border:`1px solid ${C.border}`,
                         borderRadius:'8px',zIndex:100,boxShadow:'0 8px 24px rgba(0,0,0,.12)',maxHeight:220,overflowY:'auto'}}>
              {filtPats.map(p=>(
                <div key={p.id} onClick={()=>selectPat(p)}
                  style={{padding:'0.55rem 0.75rem',cursor:'pointer',fontSize:'0.88rem',color:C.darker,
                          borderBottom:`1px solid ${C.border}`}}
                  onMouseEnter={e=>e.currentTarget.style.background='#fbf8f5'}
                  onMouseLeave={e=>e.currentTarget.style.background='#ffffff'}
                >
                  {p.name} <span style={{color:'#a39b93',fontSize:'0.75rem'}}>• {p.age} anos</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date + Time */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div>
            <label style={lbl}>Data *</label>
            <input style={inp} type="date" value={form.date} onChange={e=>set('date',e.target.value)}/>
          </div>
          <div>
            <label style={lbl}>Hora *</label>
            <input style={inp} type="time" value={form.time} onChange={e=>set('time',e.target.value)}/>
          </div>
        </div>

        {/* Templates rápidos — preenchem modalidade + duração em 1 clique */}
        <div>
          <label style={lbl}>Modelos rápidos</label>
          <div style={{display:'flex',gap:'0.4rem',flexWrap:'wrap'}}>
            {CONSULTA_TEMPLATES.map(t=>{
              const ativo = form.tipo===t.tipo && String(form.duracao_minutos)===String(t.duracao);
              return (
                <button key={t.label} type="button"
                  onClick={()=>setForm(f=>({...f,tipo:t.tipo,duracao_minutos:t.duracao}))}
                  style={{padding:'0.35rem 0.7rem',borderRadius:'999px',cursor:'pointer',fontSize:'0.76rem',fontWeight:600,
                    border:`1.5px solid ${ativo?C.primary:C.border}`,
                    background:ativo?C.primary:'#ffffff',color:ativo?'#ffffff':C.dark}}>
                  {emo(t.icon)} {emo(t.label)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Duration + Type */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div>
            <label style={lbl}>Duração</label>
            <select style={inp} value={form.duracao_minutos} onChange={e=>set('duracao_minutos',e.target.value)}>
              {[30,45,60,90,120].map(m=><option key={m} value={m}>{m} min</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Modalidade</label>
            <select style={inp} value={form.tipo} onChange={e=>set('tipo',e.target.value)}>
              <option value="presencial">Presencial</option>
              <option value="online">Online</option>
            </select>
          </div>
        </div>

        {/* Valor */}
        <div>
          <label style={lbl}>Valor (R$)</label>
          <input style={inp} type="number" step="0.01" value={form.valor_cobrado}
            onChange={e=>set('valor_cobrado',e.target.value)}/>
        </div>

        {/* Observações */}
        <div>
          <label style={lbl}>Observações</label>
          <textarea style={{...inp,resize:'vertical'}} rows={2} value={form.observacoes}
            placeholder="Observações opcionais..." onChange={e=>set('observacoes',e.target.value)}/>
        </div>

        <div style={{display:'flex',gap:'0.6rem',justifyContent:'flex-end',paddingTop:'0.5rem'}}>
          <button onClick={onClose} style={{padding:'0.55rem 1.1rem',background:'#ffffff',color:C.dark,
            border:`1px solid ${C.border}`,borderRadius:'8px',cursor:'pointer',fontWeight:600}}>Cancelar</button>
          <button onClick={submit} disabled={saving} style={{padding:'0.55rem 1.25rem',
            background:saving?'#ebe3da':C.primary,color:'#ffffff',border:'none',
            borderRadius:'8px',cursor:saving?'not-allowed':'pointer',fontWeight:600}}>
            {saving?'Agendando...':'Agendar Consulta'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── APPOINTMENT DETAIL MODAL ──────────────────────────────────────────────────
function DetailModal({ appt: initialAppt, onClose, onUpdated, onDeleted, onNavigate }) {
  const { showSuccess, showError } = useToast();
  const [appt, setAppt]           = useState(initialAppt);
  const [busy, setBusy]           = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      const res = await appointmentsService.update(appt.id, { status });
      if (res.success) {
        setAppt(res.data);
        onUpdated(res.data);
        if (status==='realizada') showSuccess('Consulta realizada — receita registrada automaticamente!');
        else showSuccess(`Status atualizado: ${STATUS_CFG[status]?.label}`);
      } else showError(res.message||'Erro ao atualizar');
    } catch(e) { showError(e.response?.data?.message||'Erro ao atualizar'); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setShowConfirm(false);
    setBusy(true);
    try {
      const res = await appointmentsService.delete(appt.id);
      if (res.success) { showSuccess('Consulta removida'); onDeleted(appt.id); onClose(); }
      else showError(res.message||'Erro ao remover');
    } catch { showError('Erro ao remover'); }
    finally { setBusy(false); }
  };

  const st   = STATUS_CFG[appt.status]||STATUS_CFG.agendada;
  const tp   = TIPO_CFG[appt.tipo]||TIPO_CFG.presencial;
  const waUrl = buildWA(appt);
  const d     = parseDT(appt.data_hora);
  const dateLong = d ? d.toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long',year:'numeric'}) : '';
  const realizada    = appt.status==='realizada';
  const cancelada    = appt.status==='cancelada'||appt.status==='faltou';
  const aguardPix    = appt.status==='aguardando_confirmacao';

  return (
    <Modal title="Consulta" onClose={onClose}>
      {/* Patient header */}
      <div style={{display:'flex',alignItems:'center',gap:'0.75rem',marginBottom:'1rem',
                   padding:'0.75rem',background:C.bg,borderRadius:'10px'}}>
        <div style={{width:40,height:40,borderRadius:'50%',background:C.primary+'25',
                     display:'flex',alignItems:'center',justifyContent:'center',
                     fontSize:'1rem',fontWeight:600,color:C.dark,flexShrink:0}}>
          {(appt.patient_name||'?').charAt(0)}
        </div>
        <div>
          <p style={{margin:0,fontWeight:600,color:C.darker,fontSize:'1rem'}}>{appt.patient_name}</p>
          <p style={{margin:0,fontSize:'0.78rem',color:'#736b64',textTransform:'capitalize'}}>{dateLong}</p>
        </div>
      </div>

      {/* Details grid */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.6rem',marginBottom:'1rem'}}>
        {[
          ['Horário', `${fmtTime(appt.data_hora)} (${appt.duracao_minutos} min)`],
          ['Modalidade', `${tp.icon} ${tp.label}`],
          ['Valor', appt.valor_cobrado ? fmtBRL(appt.valor_cobrado) : 'Não definido'],
          ['Status', <span style={{padding:'0.15rem 0.5rem',borderRadius:'999px',fontSize:'0.75rem',fontWeight:600,background:st.bg,color:st.col}}>{emo(st.label)}</span>],
        ].map(([k,v])=>(
          <div key={k} style={{background:C.bg,borderRadius:'8px',padding:'0.6rem 0.75rem'}}>
            <p style={{margin:'0 0 0.15rem',fontSize:'0.68rem',color:'#a39b93',textTransform:'uppercase',fontWeight:600}}>{k}</p>
            <p style={{margin:0,fontSize:'0.88rem',color:C.darker,fontWeight:600}}>{v}</p>
          </div>
        ))}
      </div>

      {appt.observacoes && (
        <div style={{background:C.bg,borderRadius:'8px',padding:'0.6rem 0.75rem',marginBottom:'1rem'}}>
          <p style={{margin:'0 0 0.15rem',fontSize:'0.68rem',color:'#a39b93',textTransform:'uppercase',fontWeight:600}}>Observações</p>
          <p style={{margin:0,fontSize:'0.85rem',color:C.darker}}>{appt.observacoes}</p>
        </div>
      )}

      {/* PIX pending panel */}
      {aguardPix && (
        <div style={{background:'#f8f1e2',border:'1px solid #c4933f',borderRadius:'10px',
                     padding:'0.9rem 1rem',marginBottom:'1rem'}}>
          <p style={{margin:'0 0 0.4rem',fontSize:'0.88rem',fontWeight:600,color:'#8a5f1a'}}>
            <Ico e="⏳" sp />Aguardando confirmação de pagamento PIX
          </p>
          <p style={{margin:'0 0 0.75rem',fontSize:'0.8rem',color:'#8a5f1a',lineHeight:1.4}}>
            O paciente enviou comprovante via WhatsApp. Confirme após verificar o pagamento.
          </p>
          <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
            <button disabled={busy} onClick={()=>updateStatus('confirmada')}
              style={{padding:'0.4rem 1rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.82rem',
                      background: '#ffffff',color: '#2f2a26',border: '1px solid #d9d0c6'}}>
              <Ico e="✅" sp />Confirmar consulta
            </button>
            <button disabled={busy} onClick={()=>updateStatus('cancelada')}
              style={{padding:'0.4rem 1rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.82rem',
                      background:'#f8ebe9',color:'#a8483f',border:'1px solid #c46a60'}}>
              <Ico e="❌" sp />Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Status buttons (not shown for pix-pending, realizada, or cancelada) */}
      {!realizada && !cancelada && !aguardPix && (
        <div style={{marginBottom:'1rem'}}>
          <p style={{margin:'0 0 0.5rem',fontSize:'0.73rem',fontWeight:600,color:C.dark,textTransform:'uppercase'}}>Atualizar Status</p>
          <div style={{display:'flex',gap:'0.4rem',flexWrap:'wrap'}}>
            {appt.status!=='confirmada' && (
              <button disabled={busy} onClick={()=>updateStatus('confirmada')}
                style={{padding:'0.4rem 0.85rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.82rem',
                        background: '#ffffff',color: '#2f2a26',border: '1px solid #d9d0c6'}}>Confirmar</button>
            )}
            <button disabled={busy} onClick={()=>updateStatus('realizada')}
              style={{padding:'0.4rem 0.85rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.82rem',
                      background: '#ffffff',color: '#2f2a26',border: '1px solid #d9d0c6'}}><Ico e="✅" sp />Realizada</button>
            <button disabled={busy} onClick={()=>updateStatus('faltou')}
              style={{padding:'0.4rem 0.85rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.82rem',
                      background: '#ffffff',color: '#2f2a26',border: '1px solid #d9d0c6'}}>Faltou</button>
            <button disabled={busy} onClick={()=>updateStatus('cancelada')}
              style={{padding:'0.4rem 0.85rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.82rem',
                      background:'#f8ebe9',color:'#a8483f',border:'1px solid #c46a60'}}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap',paddingTop:'0.75rem',borderTop:`1px solid ${C.border}`}}>
        {waUrl && (
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            style={{padding:'0.45rem 0.9rem',borderRadius:'8px',background: '#ffffff',color: '#2f2a26',
                    border: '1px solid #d9d0c6',textDecoration:'none',fontWeight:600,fontSize:'0.83rem'}}>
            <Ico e="💬" sp />WhatsApp
          </a>
        )}
        <button onClick={()=>{onClose();onNavigate('patient-detail',appt.patient_id);}}
          style={{padding:'0.45rem 0.9rem',borderRadius:'8px',background:C.bg,color:C.dark,
                  border:`1px solid ${C.border}`,cursor:'pointer',fontWeight:600,fontSize:'0.83rem'}}>
          <Ico e="📋" sp />Ver Prontuário
        </button>
        <button onClick={() => setShowConfirm(true)} disabled={busy}
          style={{padding:'0.45rem 0.9rem',borderRadius:'8px',background:'#ffffff',color: '#a8483f',
                  border:'1px solid #e3b3ad',cursor:'pointer',fontWeight:600,fontSize:'0.83rem',marginLeft:'auto'}}>
          <Ico e="🗑" sp />Remover
        </button>
      </div>

      {showConfirm && (
        <ConfirmModal
          title="Remover consulta"
          message="Remover esta consulta permanentemente? Esta ação não pode ser desfeita."
          confirmLabel="Remover"
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </Modal>
  );
}

// ── WEEK SUMMARY FOOTER ───────────────────────────────────────────────────────
function WeekFooter({ appointments }) {
  const realizadas = appointments.filter(a=>a.status==='realizada');
  const total      = appointments.filter(a=>!['cancelada'].includes(a.status)).length;
  const receita    = realizadas.reduce((s,a)=>s+parseFloat(a.valor_cobrado||0),0);
  return (
    <div style={{display:'flex',gap:'1rem',alignItems:'center',padding:'0.65rem 1rem',
                 background:C.bg,borderRadius:'10px',border:`1px solid ${C.border}`,
                 flexWrap:'wrap',fontSize:'0.83rem',color:C.dark}}>
      <span><Ico e="📅" /> <strong>{total}</strong> consulta{total!==1?'s':''} na semana</span>
      <span style={{color:'#a39b93'}}>·</span>
      <span><Ico e="✅" /> <strong>{realizadas.length}</strong> realizada{realizadas.length!==1?'s':''}</span>
      <span style={{color:'#a39b93'}}>·</span>
      <span><Ico e="💰" sp />Receita: <strong style={{color:C.green}}>{fmtBRL(receita)}</strong></span>
    </div>
  );
}

// ── MAIN AGENDA ───────────────────────────────────────────────────────────────
export default function Agenda({ onNavigate, patientId }) {
  const { showError, showSuccess } = useToast();

  // No celular a semana (6 colunas) fica apertada — abre no dia por padrão.
  const [view, setView]       = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth <= 768) ? 'day' : 'week');
  const [currDate, setCurrDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients]         = useState([]);
  const [settings, setSettings]         = useState(null);
  const [loading, setLoading]           = useState(true);

  // Modals
  const [newModal, setNewModal]     = useState(null);  // { date, hour, min, patient? }
  const abriuDaFicha = useRef(false);
  const [detailAppt, setDetailAppt] = useState(null);

  // Load patients + settings once
  useEffect(()=>{
    patientsService.getAll('ativo').then(r=>{ if(r.success) setPatients(r.data||[]); }).catch(()=>{});
    settingsService.get().then(r=>{ if(r.success) setSettings(r.data); }).catch(()=>{});
  },[]);

  const loadAppts = useCallback(async()=>{
    setLoading(true);
    try {
      let res;
      if (view==='week') {
        const mon = getWeekMonday(currDate);
        res = await appointmentsService.getByWeek(toISO(mon));
      } else if (view==='month') {
        const m = `${currDate.getFullYear()}-${String(currDate.getMonth()+1).padStart(2,'0')}`;
        res = await appointmentsService.getByMonth(m);
      } else {
        res = await appointmentsService.getByDate(toISO(currDate));
      }
      if (res.success) setAppointments(res.data||[]);
    } catch(e) { showError('Erro ao carregar consultas'); }
    finally { setLoading(false); }
  },[view,currDate]); // eslint-disable-line

  useEffect(()=>{ loadAppts(); },[loadAppts]);

  // Vindo da ficha da paciente (/app/agenda/:id): abre o novo agendamento já com ela.
  useEffect(()=>{
    if (!patientId || abriuDaFicha.current || !patients.length) return;
    const p = patients.find(x => Number(x.id) === Number(patientId));
    if (!p) return;
    abriuDaFicha.current = true;
    setNewModal({ date: toISO(new Date()), hour: 8, min: 0, patient: p });
  },[patientId, patients]);

  // Navigation
  const prev = () => setCurrDate(d=>{
    const n=new Date(d);
    if (view==='week') n.setDate(n.getDate()-7);
    else if (view==='month') n.setMonth(n.getMonth()-1);
    else n.setDate(n.getDate()-1);
    return n;
  });
  const next = () => setCurrDate(d=>{
    const n=new Date(d);
    if (view==='week') n.setDate(n.getDate()+7);
    else if (view==='month') n.setMonth(n.getMonth()+1);
    else n.setDate(n.getDate()+1);
    return n;
  });
  const goToday = () => setCurrDate(new Date());

  // Header title
  const headerTitle = () => {
    if (view==='week') {
      const mon = getWeekMonday(currDate);
      const sat = new Date(mon); sat.setDate(sat.getDate()+5);
      const same = mon.getMonth()===sat.getMonth();
      if (same) return `${mon.getDate()} – ${sat.getDate()} de ${MONTH_FULL[mon.getMonth()]} de ${mon.getFullYear()}`;
      return `${mon.getDate()} ${MONTH_FULL[mon.getMonth()].slice(0,3)} – ${sat.getDate()} ${MONTH_FULL[sat.getMonth()].slice(0,3)} ${sat.getFullYear()}`;
    }
    if (view==='month') return `${MONTH_FULL[currDate.getMonth()]} de ${currDate.getFullYear()}`;
    return currDate.toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long',year:'numeric'});
  };

  const onApptUpdated = (updated) => {
    setAppointments(prev => prev.map(a => a.id===updated.id ? updated : a));
    if (detailAppt?.id===updated.id) setDetailAppt(updated);
  };

  // Reagendar arrastando: atualização otimista + rollback se o backend recusar
  // (conflito de horário). Recebe o novo "YYYY-MM-DD HH:MM:00".
  const onApptReschedule = async (appt, novoDataHora) => {
    if (novoDataHora === appt.data_hora) return;
    const anterior = appt;
    setAppointments(prev => prev.map(a => a.id===appt.id ? { ...a, data_hora: novoDataHora } : a));
    try {
      const res = await appointmentsService.update(appt.id, { data_hora: novoDataHora });
      if (res.success) {
        setAppointments(prev => prev.map(a => a.id===appt.id ? res.data : a).sort((a,b)=>a.data_hora.localeCompare(b.data_hora)));
        showSuccess(`${appt.patient_name} reagendado para ${fmtTime(novoDataHora)}`);
      } else {
        setAppointments(prev => prev.map(a => a.id===appt.id ? anterior : a));
        showError(res.error || res.message || 'Não foi possível reagendar');
      }
    } catch (e) {
      setAppointments(prev => prev.map(a => a.id===appt.id ? anterior : a));
      showError(e.response?.data?.error || 'Conflito de horário — reagendamento desfeito');
    }
  };

  const onApptCreated = (newAppt) => {
    setAppointments(prev => [...prev, newAppt].sort((a,b)=>a.data_hora.localeCompare(b.data_hora)));
  };

  const onApptDeleted = (id) => {
    setAppointments(prev => prev.filter(a=>a.id!==id));
  };

  const onSlotClick = (date, hour, min) => {
    setNewModal({ date, hour, min });
  };

  const onMonthDayClick = (date) => {
    setCurrDate(date);
    setView('day');
  };

  const TAB = { padding:'0.4rem 0.9rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.83rem',border:'none' };

  return (
    <div style={{maxWidth:'1200px',margin:'0 auto',padding:'0 0.25rem',display:'flex',flexDirection:'column',gap:'1rem'}}>
      {/* ── Header ── */}
      <div style={{display:'flex',alignItems:'center',gap:'0.75rem',flexWrap:'wrap'}}>
        {/* View tabs */}
        <div style={{display:'flex',background:C.bg,borderRadius:'8px',padding:'3px',border:`1px solid ${C.border}`,gap:'2px',flexShrink:0}}>
          {[['week','Semana'],['day','Dia'],['month','Mês']].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)} style={{
              ...TAB,
              background:view===v?C.primary:'transparent',
              color:view===v?'#ffffff':C.dark,
            }}>{l}</button>
          ))}
        </div>

        {/* Navigation */}
        <button onClick={prev} style={{...TAB,background:'#ffffff',color:C.dark,border:`1px solid ${C.border}`}}>‹</button>
        <button onClick={goToday} style={{...TAB,background:'#ffffff',color:C.dark,border:`1px solid ${C.border}`,fontSize:'0.78rem'}}>Hoje</button>
        <button onClick={next} style={{...TAB,background:'#ffffff',color:C.dark,border:`1px solid ${C.border}`}}>›</button>

        {/* Title */}
        <h2 style={{margin:0,fontSize:'1rem',fontWeight:600,color:C.darker,flex:1,textTransform:'capitalize',minWidth:'200px'}}>
          {headerTitle()}
        </h2>

        {/* New appt button */}
        <button onClick={()=>setNewModal({date:toISO(currDate),hour:8,min:0})}
          style={{padding:'0.5rem 1.1rem',background:C.primary,color:'#ffffff',border:'none',
                  borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'0.85rem',flexShrink:0}}>
          <Ico e="➕" sp />Nova Consulta
        </button>
      </div>

      {/* ── Legend ── */}
      <div style={{display:'flex',gap:'1rem',fontSize:'0.75rem',color:'#736b64',alignItems:'center',flexWrap:'wrap'}}>
        <span style={{display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:10,height:10,borderRadius:'50%',background:C.green,display:'block'}}/> Presencial
        </span>
        <span style={{display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:10,height:10,borderRadius:'50%',background:C.blue,display:'block'}}/> Online
        </span>
        <span style={{display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:10,height:10,borderRadius:'50%',background:'#c4933f',display:'block'}}/> <Ico e="⏳" sp />Ag. PIX
        </span>
        <span style={{display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:10,height:10,borderRadius:'50%',background:'#b8c7d8',display:'block'}}/> Cancelada/Faltou
        </span>
        <span style={{display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:14,height:10,borderRadius:'2px',background:'#f6f0ea',border:'1px solid #d9d0c6',display:'block'}}/> Fora do expediente
        </span>
        {loading && <span style={{color:C.primary}}>Carregando...</span>}
      </div>

      {/* ── Views ── */}
      {view==='week' && (
        <WeekView appointments={appointments} currentDate={currDate}
          onSlotClick={onSlotClick} onApptClick={setDetailAppt} onReschedule={onApptReschedule} settings={settings}/>
      )}
      {view==='day' && (
        <DayView appointments={appointments} currentDate={currDate}
          onSlotClick={onSlotClick} onApptClick={setDetailAppt} onReschedule={onApptReschedule} settings={settings}/>
      )}
      {view==='month' && (
        <MonthView appointments={appointments} currentDate={currDate}
          onDayClick={onMonthDayClick}/>
      )}

      {/* ── Week footer ── */}
      {view==='week' && <WeekFooter appointments={appointments}/>}

      {/* ── Modals ── */}
      {newModal && (
        <NewApptModal
          onClose={()=>setNewModal(null)}
          onCreated={onApptCreated}
          initDate={newModal.date}
          initHour={newModal.hour}
          initMin={newModal.min}
          initPatient={newModal.patient}
          patients={patients}
          settings={settings}
        />
      )}
      {detailAppt && (
        <DetailModal
          appt={detailAppt}
          onClose={()=>setDetailAppt(null)}
          onUpdated={onApptUpdated}
          onDeleted={onApptDeleted}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}
